/**
 * Visual styling for `order_items.invoice_composite_status` (and common Stripe/legacy
 * status strings) on pills in the dashboard, modals, and order detail.
 */
const PILL =
  'inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium leading-tight capitalize ';

const COMPOSITE_MAP: Record<string, string> = {
  not_invoiced: `${PILL} bg-slate-100 text-slate-800 border-slate-200`,
  partially_invoiced: `${PILL} bg-amber-50 text-amber-900 border-amber-200`,
  invoiced: `${PILL} bg-sky-50 text-sky-900 border-sky-200`,
  partially_paid: `${PILL} bg-violet-50 text-violet-900 border-violet-200`,
  paid: `${PILL} bg-emerald-50 text-emerald-900 border-emerald-200`,
  overdue: `${PILL} bg-orange-50 text-orange-900 border-orange-200`,
  uncollectible: `${PILL} bg-red-50 text-red-900 border-red-200`,
  /** Legacy Stripe: collected outside automatic flow. */
  manual: `${PILL} bg-fuchsia-50 text-fuchsia-900 border-fuchsia-200`,
  /** Bug 20222 — sync in flight / awaiting reconciliation; not yet a real invoice. */
  pending_sync: `${PILL} bg-amber-50 text-amber-900 border-amber-200`,
};

/** Stripe / free-text status when composite is not set. */
const STRIPE_FALLBACK: Record<string, string> = {
  open: `${PILL} bg-amber-50 text-amber-900 border-amber-200`,
  draft: `${PILL} bg-gray-100 text-gray-800 border-gray-200`,
  void: `${PILL} bg-stone-100 text-stone-800 border-stone-200`,
  paid: COMPOSITE_MAP.paid,
  uncollectible: COMPOSITE_MAP.uncollectible,
  deleted: `${PILL} bg-stone-100 text-stone-800 border-stone-200`,
  manual: COMPOSITE_MAP.manual,
};

function normalizeKey(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s).toLowerCase().replace(/\s+/g, '_');
}

/**
 * @param emptyAsNotInvoiced — when composite is null/empty, use `not_invoiced` paint (e.g. modal "Not Invoiced").
 */
export function invoiceCompositeStatusPillClass(
  status: string | null | undefined,
  options?: { emptyAsNotInvoiced?: boolean },
): string {
  if (options?.emptyAsNotInvoiced && (status == null || String(status).trim() === '')) {
    return COMPOSITE_MAP.not_invoiced;
  }
  if (status == null || String(status).trim() === '') {
    return `${PILL} bg-gray-100 text-gray-700 border-gray-200`;
  }
  const key = normalizeKey(status);
  if (COMPOSITE_MAP[key]) {
    return COMPOSITE_MAP[key];
  }
  if (STRIPE_FALLBACK[key]) {
    return STRIPE_FALLBACK[key];
  }
  return `${PILL} bg-slate-50 text-slate-800 border-slate-200`;
}

/**
 * Shown label for composite keys (uses backend snake_case; legacy may be raw).
 */
export function formatInvoiceStatusLabel(
  status: string | null | undefined,
  fallback: string = 'N/A',
): string {
  if (status == null || String(status).trim() === '') {
    return fallback;
  }
  return String(status).replace(/_/g, ' ');
}
